<!-- Selector de tipo de trabajo -->

<template>
  <div class="service-selector-container">
    <h2 class="text-lg font-semibold leading-tight pt-2">Items</h2>
    
    <!-- Team Filter Section -->
    <div class="team-selector-section">
      <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
        Equipo <span class="text-red-500 dark:text-red-400">*</span>
      </label>
      <select
        v-model="selectedTeam"
        @change="onTeamChange"
        class="form-control select-arrow"
        :disabled="loadingTeams"
      >
        <option value="">{{ loadingTeams ? 'Cargando equipos...' : 'Seleccionar equipo' }}</option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.icon }} {{ team.name }}
        </option>
      </select>
    </div>
    
    <!-- Services Section - Only show if team is selected -->
    <div v-if="selectedTeam && !loadingGroups && !loadingItems" class="services-section">
      <div v-for="team in filteredServices" :key="team.team" class="team-services">
        <h3 class="team-name">{{ team.team }}</h3>
        <div v-for="group in team.groups" :key="group.group" class="service-group">
          <label class="group-label">{{ group.group }}</label>
          <div class="service-select-wrapper">
            <select
              v-model="localSelectedService"
              @change="emitSelectedService"
              class="form-control select-arrow"
            >
              <option value="" disabled>Seleccionar servicio</option>
              <option v-for="service in group.items" :key="service.name" :value="service">
                {{ service.name }}
              </option>
            </select>
            <button @click="emitAddItem" :disabled="!localSelectedService" class="add-item-btn">
              <span class="material-icons">add</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Loading indicator -->
    <div v-else-if="loadingGroups || loadingItems" class="no-team-selected">
      <div class="placeholder-content">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        <p>Cargando servicios...</p>
      </div>
    </div>
    
    <!-- Placeholder when no team is selected -->
    <div v-else class="no-team-selected">
      <div class="placeholder-content">
        <span class="material-icons-outlined">group</span>
        <p>Selecciona un equipo para ver los servicios disponibles</p>
      </div>
    </div>
  </div>
</template>

<script>
import { adminAPI } from '@/services/api'

export default {
  props: {
    services: Array,
    selectedService: Object,
  },
  data() {
    return {
      localSelectedService: this.selectedService, // Create a local copy of the prop
      selectedTeam: '', // Track selected team filter
      teams: [], // Teams loaded from API
      groups: [], // Groups for selected team
      items: [], // Items for selected group
      loadingTeams: false,
      loadingGroups: false,
      loadingItems: false,
    };
  },
  computed: {
    filteredServices() {
      if (!this.selectedTeam || this.groups.length === 0) {
        return [];
      }
      // Create structure similar to original services prop
      const teamObj = this.teams.find(t => t.id === this.selectedTeam);
      if (!teamObj) return [];
      
      return [{
        team: teamObj.name,
        groups: this.groups.map(group => ({
          group: group.name,
          items: this.items.filter(item => item.group_id === group.id).map(item => ({
            name: item.name,
            hours: item.hours,
            defaultHours: item.hours,
            selectedTeam: teamObj.name
          }))
        })).filter(group => group.items.length > 0)
      }];
    }
  },
  watch: {
    // Watch for changes in the prop and update the local copy
    selectedService(newVal) {
      this.localSelectedService = newVal;
    },
  },
  async mounted() {
    // Load teams on component mount
    await this.fetchTeams();
  },
  methods: {
    async fetchTeams() {
      this.loadingTeams = true;
      try {
        const data = await adminAPI.getTeams();
        if (data.success) {
          this.teams = data.teams || [];
        } else {
          console.error('Error loading teams:', data.message);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        this.loadingTeams = false;
      }
    },
    
    async fetchGroups(teamId) {
      this.loadingGroups = true;
      this.groups = [];
      this.items = [];
      try {
        const data = await adminAPI.getGroups({ team_id: teamId });
        if (data.success) {
          this.groups = data.groups || [];
          // Fetch items for all groups of this team
          await this.fetchItemsForTeam(teamId);
        } else {
          console.error('Error loading groups:', data.message);
        }
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        this.loadingGroups = false;
      }
    },
    
    async fetchItemsForTeam(teamId) {
      this.loadingItems = true;
      try {
        const data = await adminAPI.getItems({ team_id: teamId, limit: 100 });
        if (data.success) {
          this.items = data.items || [];
        } else {
          console.error('Error loading items:', data.message);
        }
      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        this.loadingItems = false;
      }
    },
    
    async onTeamChange() {
      // Clear selected service when team changes
      this.localSelectedService = null;
      this.$emit("service-selected", null);
      
      // Fetch groups and items for selected team
      if (this.selectedTeam) {
        await this.fetchGroups(this.selectedTeam);
      } else {
        this.groups = [];
        this.items = [];
      }
    },
    
    emitSelectedService() {
      // Emit the selected service and let the parent handle team information
      const teamObj = this.teams.find(t => t.id === this.selectedTeam);
      const teamName = teamObj ? teamObj.name : this.selectedTeam;
      this.$emit("service-selected", this.localSelectedService, teamName);
    },
    
    emitAddItem() {
      this.$emit('add-item');
    },
  },
};
</script>